import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { isModuleKey, toggleModuleKeys } from '@heyroot/contracts/tenants/entitlements/module-keys';
import {
  getModuleRegistryEntry,
  type ModuleRegistryEntry,
} from '@heyroot/contracts/tenants/entitlements/registry';
import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { SkeletonTable } from '@heyroot/ui/primitives/Skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import { fetchModuleMatrix, fetchMatrixBusinessTypes, queryKeys } from '../lib/api';

// Exemplar: docs/design-system/exemplars -- a dense cross-entity matrix,
// closest to the tenant list's own table (tenants-list.tsx), extended
// horizontally by module key rather than by a fixed column set.

const PAGE_SIZE = 50;
const TENANT_STATUSES = ['trial', 'active', 'suspended', 'deleted'] as const;
// A platform record (`regulated_profile`, #15687) is recorded on the tenant page, so the matrix has no
// column for it. Column count and cells both derive from this one list (matrix-repo.ts filters the same).
const MATRIX_KEYS = toggleModuleKeys();

// §4.8 park-inbox-entitlement-gate: literal, non-inferred registry metadata
// for the sticky header row -- never per-cell (rows x ~40 columns would
// repeat it absurdly). The isModuleKey() guard here isn't about
// getModuleRegistryEntry's PARAMETER -- MODULE_KEYS.map already yields a real
// ModuleKey, so that's satisfied at compile time. It's about the RETURN type:
// getModuleRegistryEntry asserts a non-optional ModuleRegistryEntry, true
// only for a key the CURRENT registry snapshot actually has -- a monorepo
// package-version skew can make the lookup undefined at runtime despite that
// assertion (same posture registry.ts:588-594 documents on moduleLabel()).
// Render no chip rather than trust an assertion that might be lying.
function moduleRegistryEntry(key: string): ModuleRegistryEntry | undefined {
  return isModuleKey(key) ? getModuleRegistryEntry(key) : undefined;
}

function cellVariant(mode: string): BadgeVariant {
  switch (mode) {
    case 'off':
      return 'neutral';
    case 'locked':
      return 'warning';
    case 'view':
      return 'info';
    default:
      return 'success'; // full
  }
}

export function ModuleMatrixPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [offset, setOffset] = useState(0);

  const params = {
    limit: PAGE_SIZE,
    offset,
    ...(status ? { status } : {}),
    ...(businessType ? { businessType } : {}),
  };
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.moduleMatrix(params),
    queryFn: () => fetchModuleMatrix(params),
    placeholderData: keepPreviousData,
  });
  // Filter-option source: distinct business-type SLUGS actually present on
  // tenants.tenants (fix round 1) -- includes 'mixed', the column default
  // every self-serve tenant carries, unlike the published-types list
  // tenant-new.tsx's create form uses.
  const { data: businessTypesData } = useQuery({
    queryKey: ['module-matrix-business-types'],
    queryFn: fetchMatrixBusinessTypes,
    staleTime: Infinity,
  });
  const businessTypeOptions = businessTypesData?.businessTypes ?? [];

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-heading-2 text-primary">Module Matrix</h1>
        <p className="mt-1 text-body-sm text-muted">
          Cross-tenant module access, read-only. A cell links to that tenant's Modules tab, where
          entitlement writes run through the mutation engine.
        </p>
      </div>

      <div className="flex items-center gap-2.5">
        <div className="w-44">
          <Select
            value={status}
            onValueChange={(v) => {
              setStatus(v === 'all' ? '' : v);
              setOffset(0);
            }}
          >
            <SelectTrigger aria-label="Filter by tenant status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {TENANT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-52">
          <Select
            value={businessType}
            onValueChange={(v) => {
              setBusinessType(v === 'all' ? '' : v);
              setOffset(0);
            }}
          >
            <SelectTrigger aria-label="Filter by business type">
              <SelectValue placeholder="All business types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All business types</SelectItem>
              {businessTypeOptions.map((bt) => (
                <SelectItem key={bt} value={bt}>
                  {bt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isError ? (
        <ErrorState title="Could not load the module matrix" onRetry={() => void refetch()} />
      ) : !isPending && rows.length === 0 ? (
        <EmptyState
          title="No tenants match this filter"
          description="Try a different status filter."
        />
      ) : (
        <>
          {/* §4.8 park-inbox-entitlement-gate disclosure: matrix-repo.ts reads
              raw access_mode with no activation-window filter (defaults every
              missing row to 'off'), while the real runtime resolver applies
              both default grants and activation windows -- a real,
              pre-existing accuracy gap this plan discloses rather than fixes
              (out of scope here; tracked as #10996). Frontend VITE_* build
              flags are a second, independent gate this page has no
              read/write access to at all. This paragraph is test-locked by
              module-matrix.test.tsx's "shows the fixed disclosure copy" test
              -- matrix-repo.ts's cell-fill loop carries the matching
              breadcrumb back to here. */}
          <p className="text-body-sm text-muted">
            Cells show stored modes, with SMS restricted until staff approval. Missing rows display
            as Off; default grants and activation windows are not reflected. Some features also
            require a frontend build flag, which this page cannot read or change.
          </p>

          {/* Horizontal scroll lives in its OWN overflow-x container (step 4) --
              the page body must never scroll horizontally. */}
          <div className="overflow-x-auto rounded-control border border-border">
            <Table dense>
              <TableHead>
                <tr>
                  <TableHeaderCell className="sticky left-0 bg-surface-elevated">
                    Tenant
                  </TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  {MATRIX_KEYS.map((key) => {
                    const entry = moduleRegistryEntry(key);
                    return (
                      <TableHeaderCell key={key} className="align-top">
                        <div className="flex flex-col gap-1">
                          <span className="font-mono">{key}</span>
                          {entry && (
                            <div className="flex flex-col items-start gap-1 whitespace-nowrap normal-case tracking-normal">
                              <Badge variant="neutral">
                                Default access: {entry.defaultGranted ? 'full' : 'off'}
                              </Badge>
                              <Badge variant="neutral">Visibility: {entry.visibility}</Badge>
                              {entry.comingSoon === true && (
                                <Badge variant="info">Coming soon</Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </TableHeaderCell>
                    );
                  })}
                </tr>
              </TableHead>
              <TableBody>
                {isPending ? (
                  <SkeletonTable rows={6} cols={2 + MATRIX_KEYS.length} />
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.tenantId} className="hover:bg-surface-elevated">
                      <TableCell className="sticky left-0 bg-surface min-w-0 truncate">
                        {row.displayName}
                      </TableCell>
                      <TableCell className="text-caption text-secondary">{row.status}</TableCell>
                      {MATRIX_KEYS.map((key) => {
                        // A tenant with no row for a module renders Off -- the
                        // repo already fills every key explicitly (step 1), so
                        // this is never re-derived here, only read.
                        const mode = row.cells[key] ?? 'off';
                        // design-exception: the cell's Badge must itself be the click
                        // target (a per-cell link, step 3), with no surrounding button
                        // chrome -- <Button> would fight the badge's compact pill look,
                        // same rationale as tenants-list.tsx's sort-header buttons.
                        return (
                          <TableCell key={key}>
                            <button
                              type="button"
                              aria-label={`${row.displayName} — ${key}: ${mode}. Open Modules tab.`}
                              onClick={() =>
                                void navigate({
                                  to: '/tenants/$tenantId',
                                  params: { tenantId: row.tenantId },
                                  search: { tab: 'modules' },
                                })
                              }
                              className="bg-transparent border-none p-0 cursor-pointer"
                            >
                              <Badge variant={cellVariant(mode)}>{mode}</Badge>
                            </button>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {!isPending && total > PAGE_SIZE && (
            <div className="flex items-center justify-end gap-2">
              <span className="text-body-sm text-muted">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
